import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiSend, FiPlay, FiCopy, FiCheck, FiLock, FiAlertCircle, FiClock, FiShield } from 'react-icons/fi';
import toast from 'react-hot-toast';

export function ApiTestRunner({ endpoint, canTest, defaultBaseUrl }) {
  const [baseUrl, setBaseUrl] = useState(
    defaultBaseUrl ||
      process.env.REACT_APP_BASE_URL?.replace(/\/api\/?$/, '') ||
      'http://localhost:8080'
  );
  const [pathParams, setPathParams] = useState({});
  const [queryParams, setQueryParams] = useState({});
  const [headers, setHeaders] = useState({});
  const [requestBody, setRequestBody] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Initialize parameters from spec
  useEffect(() => {
    const spec = endpoint.endpointSpec;
    const initialPathParams = {};
    const initialQueryParams = {};

    const pathMatches = endpoint.path.match(/\{([^}]+)\}/g);
    if (pathMatches) {
      pathMatches.forEach((match) => {
        const paramName = match.replace(/[{}]/g, '');
        initialPathParams[paramName] = '';
      });
    }

    if (spec && Array.isArray(spec.parameters)) {
      spec.parameters.forEach((param) => {
        if (param.in === 'path') {
          initialPathParams[param.name] = param.example || param.schema?.default || '';
        } else if (param.in === 'query') {
          initialQueryParams[param.name] = param.example || param.schema?.default || '';
        }
      });
    }

    const token = localStorage.getItem('authToken');
    setHeaders({
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    });

    setPathParams(initialPathParams);
    setQueryParams(initialQueryParams);

    if (['POST', 'PUT', 'PATCH'].includes(endpoint.httpMethod.toUpperCase())) {
      const content = spec?.requestBody?.content;
      const jsonContent = content?.['application/json'];
      if (jsonContent?.example) {
        setRequestBody(JSON.stringify(jsonContent.example, null, 2));
      } else if (jsonContent?.schema) {
        setRequestBody(JSON.stringify(generateSampleFromSchema(jsonContent.schema), null, 2));
      } else {
        setRequestBody('{\n  \n}');
      }
    } else {
      setRequestBody('');
    }

    setResponse(null);
    setError(null);
  }, [endpoint]);

  const handleSend = async () => {
    if (!canTest) return;
    setIsLoading(true);
    setError(null);
    setResponse(null);

    let finalPath = endpoint.path;
    Object.entries(pathParams).forEach(([k, v]) => {
      finalPath = finalPath.replace(`{${k}}`, encodeURIComponent(v));
    });

    let fullUrl = baseUrl.replace(/\/+$/, '') + '/' + finalPath.replace(/^\/+/, '');
    const activeQueryParams = Object.entries(queryParams).filter(([_, v]) => v !== '');
    if (activeQueryParams.length > 0) {
      const qs = new URLSearchParams();
      activeQueryParams.forEach(([k, v]) => qs.append(k, v));
      fullUrl += '?' + qs.toString();
    }

    const startTime = performance.now();
    try {
      let parsedBody = undefined;
      if (['POST', 'PUT', 'PATCH'].includes(endpoint.httpMethod.toUpperCase()) && requestBody.trim()) {
        try {
          parsedBody = JSON.parse(requestBody);
        } catch (e) {
          throw new Error('Invalid JSON in Request Body: ' + e.message);
        }
      }

      const res = await axios({
        method: endpoint.httpMethod.toLowerCase(),
        url: fullUrl,
        headers,
        data: parsedBody,
        validateStatus: () => true, // Don't throw for 4xx/5xx so developer can inspect response
      });

      const endTime = performance.now();
      setResponse({
        status: res.status,
        statusText: res.statusText || (res.status === 200 ? 'OK' : 'Response'),
        headers: res.headers,
        data: res.data,
        timeMs: Math.round(endTime - startTime),
      });
    } catch (err) {
      setError(err.message || 'Network error executing request');
    } finally {
      setIsLoading(false);
    }
  };

  const copyResponse = () => {
    if (response) {
      navigator.clipboard.writeText(JSON.stringify(response.data, null, 2));
      setCopied(true);
      toast.success('Response copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!canTest) {
    return (
      <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 flex items-center gap-3">
        <FiLock size={18} className="shrink-0 text-amber-600" />
        <p className="text-sm">
          Interactive API testing is restricted for this documentation. Contact the document owner or an ERP Administrator to request testing permissions.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-3 border-t border-slate-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-700">
          <FiPlay className="text-blue-600" />
          <span>Interactive Test Runner</span>
        </div>
        <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200/60">
          <FiShield size={12} /> Live Testing Enabled
        </span>
      </div>

      {/* Target Base URL */}
      <div>
        <label className="block text-xs font-bold text-slate-700 mb-1">
          Server / Base URL
        </label>
        <input
          type="text"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder="http://localhost:8080"
          className="w-full text-xs font-mono px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition"
        />
      </div>

      {/* Path Parameters */}
      {Object.keys(pathParams).length > 0 && (
        <div className="space-y-2">
          <label className="block text-xs font-bold text-slate-700">
            Path Parameters
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.keys(pathParams).map((param) => (
              <div key={param} className="flex items-center gap-2 text-xs">
                <span className="font-mono text-slate-600 font-semibold min-w-[70px]">{param}:</span>
                <input
                  type="text"
                  value={pathParams[param]}
                  onChange={(e) => setPathParams({ ...pathParams, [param]: e.target.value })}
                  placeholder={`value for {${param}}`}
                  className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-900 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Query Parameters */}
      {Object.keys(queryParams).length > 0 && (
        <div className="space-y-2">
          <label className="block text-xs font-bold text-slate-700">
            Query Parameters
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.keys(queryParams).map((param) => (
              <div key={param} className="flex items-center gap-2 text-xs">
                <span className="font-mono text-slate-600 font-semibold min-w-[70px]">{param}:</span>
                <input
                  type="text"
                  value={queryParams[param]}
                  onChange={(e) => setQueryParams({ ...queryParams, [param]: e.target.value })}
                  placeholder="value"
                  className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-900 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Request Body (for POST, PUT, PATCH) */}
      {['POST', 'PUT', 'PATCH'].includes(endpoint.httpMethod.toUpperCase()) && (
        <div>
          <label className="block text-xs font-bold text-slate-700 mb-1">
            Request Body (JSON)
          </label>
          <textarea
            rows={5}
            value={requestBody}
            onChange={(e) => setRequestBody(e.target.value)}
            className="w-full text-xs font-mono p-3 rounded-lg border border-slate-800 bg-slate-900 text-emerald-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            spellCheck={false}
          />
        </div>
      )}

      {/* Send Button */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSend}
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg shadow-sm transition disabled:opacity-50"
        >
          <FiSend className={isLoading ? 'animate-spin' : ''} />
          <span>{isLoading ? 'Executing Request...' : 'Send Request'}</span>
        </button>
      </div>

      {/* Error Message */}
      {error && !response && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-rose-50 text-rose-700 text-xs border border-rose-200">
          <FiAlertCircle size={15} />
          <span>{error}</span>
        </div>
      )}

      {/* Response Panel */}
      {response && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs">
            <div className="flex items-center gap-3">
              <span
                className={`px-2 py-0.5 rounded font-mono font-bold text-xs border ${
                  response.status >= 200 && response.status < 300
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : response.status >= 400 && response.status < 500
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-rose-50 text-rose-700 border-rose-200'
                }`}
              >
                {response.status} {response.statusText}
              </span>
              <span className="flex items-center gap-1 text-slate-500 text-[11px] font-medium">
                <FiClock size={12} /> {response.timeMs} ms
              </span>
            </div>
            <button
              onClick={copyResponse}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-[11px] font-medium transition shadow-sm"
            >
              {copied ? <FiCheck className="text-emerald-600" /> : <FiCopy />}
              <span>{copied ? 'Copied' : 'Copy Response'}</span>
            </button>
          </div>
          <div className="p-3.5 bg-slate-900 max-h-[350px] overflow-auto">
            <pre className="text-xs font-mono text-emerald-300 whitespace-pre-wrap leading-relaxed">
              {typeof response.data === 'object'
                ? JSON.stringify(response.data, null, 2)
                : String(response.data)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

function generateSampleFromSchema(schema) {
  if (!schema) return {};
  if (schema.example !== undefined) return schema.example;
  if (schema.type === 'object' && schema.properties) {
    const obj = {};
    Object.keys(schema.properties).forEach((k) => {
      obj[k] = generateSampleFromSchema(schema.properties[k]);
    });
    return obj;
  }
  if (schema.type === 'array') {
    return [generateSampleFromSchema(schema.items || {})];
  }
  if (schema.type === 'string') return 'string';
  if (schema.type === 'number' || schema.type === 'integer') return 0;
  if (schema.type === 'boolean') return true;
  return null;
}

export default ApiTestRunner;
